import React, { useState, useEffect } from 'react';
import { getProjects, createProject, deleteProject } from '../services/api';
import Button from '../components/ui/Button';
import { Trash2, Plus, Loader } from 'lucide-react';

const Admin = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        description: '',
        location: '',
        year: '',
        client: '',
        status: 'Completed',
        image: null
    });

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const data = await getProjects();
            setProjects(data);
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({ ...prev, image: e.target.files[0] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const data = new FormData();
        Object.keys(formData).forEach(key => {
            data.append(key, formData[key]);
        });

        try {
            await createProject(data);
            await fetchProjects();
            setFormData({
                title: '',
                category: '',
                description: '',
                location: '',
                year: '',
                client: '',
                status: 'Completed',
                image: null
            });
            alert('Project created successfully!');
        } catch (error) {
            console.error("Error creating project:", error);
            alert('Failed to create project.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await deleteProject(id);
                await fetchProjects();
            } catch (error) {
                console.error("Error deleting project:", error);
                alert('Failed to delete project.');
            }
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 p-8">
            <div className="max-w-6xl mx-auto">
                <h1 className="text-3xl font-bold text-slate-900 mb-8">Admin Dashboard</h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Form Section */}
                    <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-md h-fit">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-gold-500" /> Add New Project
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                                <input required name="title" value={formData.title} onChange={handleInputChange} className="w-full p-2 border rounded" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                                <input required name="category" value={formData.category} onChange={handleInputChange} className="w-full p-2 border rounded" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                                <textarea required name="description" value={formData.description} onChange={handleInputChange} rows="3" className="w-full p-2 border rounded" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                                    <input name="location" value={formData.location} onChange={handleInputChange} className="w-full p-2 border rounded" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                                    <input name="year" value={formData.year} onChange={handleInputChange} className="w-full p-2 border rounded" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
                                <input name="client" value={formData.client} onChange={handleInputChange} className="w-full p-2 border rounded" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Image</label>
                                <input required type="file" onChange={handleFileChange} className="w-full p-2 border rounded" />
                            </div>
                            <Button type="submit" variant="primary" className="w-full" disabled={loading}>
                                {loading ? <Loader className="animate-spin" /> : 'Create Project'}
                            </Button>
                        </form>
                    </div>

                    {/* List Section */}
                    <div className="lg:col-span-2 space-y-4">
                        <h2 className="text-xl font-bold mb-6">Existing Projects</h2>
                        {projects.map(project => (
                            <div key={project.id} className="bg-white p-4 rounded-lg shadow-sm flex items-center gap-4">
                                <img src={project.image} alt={project.title} className="w-20 h-20 object-cover rounded" />
                                <div className="flex-1">
                                    <h3 className="font-bold text-slate-900">{project.title}</h3>
                                    <p className="text-sm text-slate-500">{project.category}</p>
                                </div>
                                <button onClick={() => handleDelete(project.id)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>
                        ))}
                        {projects.length === 0 && !loading && (
                            <p className="text-slate-500 text-center py-8">No projects found.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Admin;
